import { FormEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useKey } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useRouter } from 'next/router'

import CloseButton from '@/components/base/close-button'
import SVGIcon from '@/components/base/svg-icon'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type SearchOverlayProps = {
  open: boolean
  onClose: () => void
}

const SearchOverlay = ({ open, onClose }: SearchOverlayProps) => {
  const { strings } = useStore()
  const router = useRouter()
  const [mounted, setMounted] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  // Portal mount
  useEffect(() => {
    setMounted(true)
  }, [])

  // GSAP open/close animation
  useGSAP(
    () => {
      if (!rootRef.current) return
      gsap.to(rootRef.current, {
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        duration: 0.3,
        ease: 'power2.out'
      })
    },
    { dependencies: [open] }
  )

  // Focus input on open
  useEffect(() => {
    if (open && inputRef.current) {
      // Small delay to let animation start
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [open])

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return

    const prevBodyOverflow = document.body.style.overflow
    const prevHtmlOverflow = document.documentElement.style.overflow

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prevBodyOverflow
      document.documentElement.style.overflow = prevHtmlOverflow
    }
  }, [open])

  // Escape key
  useKey(
    'Escape',
    () => {
      if (open) onClose()
    },
    {},
    [open, onClose]
  )

  // Focus trap: Tab cycling between input and close button
  useKey(
    'Tab',
    (e) => {
      if (!open) return

      const focusable = [inputRef.current, closeButtonRef.current].filter(
        Boolean
      ) as HTMLElement[]
      if (focusable.length === 0) return

      const activeIndex = focusable.indexOf(
        document.activeElement as HTMLElement
      )

      if (e.shiftKey) {
        e.preventDefault()
        const prevIndex =
          activeIndex <= 0 ? focusable.length - 1 : activeIndex - 1
        focusable[prevIndex].focus()
      } else {
        if (activeIndex === focusable.length - 1) {
          e.preventDefault()
          focusable[0].focus()
        }
      }
    },
    {},
    [open]
  )

  // Close overlay and navigate on route change
  useEffect(() => {
    const handleRouteChange = () => {
      onClose()
    }
    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router, onClose])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) return

    onClose()
    router.push({
      pathname: '/search',
      query: { q: trimmed }
    })
  }

  if (!mounted) return null

  return createPortal(
    <div
      ref={rootRef}
      className={cn(
        'pointer-events-none fixed inset-0 z-[110] flex items-start justify-center opacity-0',
        'bg-surface-primary-dark/60 backdrop-blur-[17px] dark:bg-surface-primary-light/60'
      )}
      role="dialog"
      aria-modal="true"
      aria-label={strings?.labels?.search || 'Cerca'}
      aria-hidden={!open}
    >
      <CloseButton
        ref={closeButtonRef}
        onClick={onClose}
        label={strings?.labels?.close_search || 'Chiudi ricerca'}
      />

      <div className="flex w-full max-w-[800px] flex-col items-center px-padding-sm pt-[var(--header-height)]">
        <form onSubmit={handleSubmit} role="search" className="w-full">
          <div className="relative w-full">
            <label htmlFor="search-input" className="sr-only">
              {strings?.labels?.search_placeholder || 'Cerca nel sito'}
            </label>
            <input
              ref={inputRef}
              id="search-input"
              type="search"
              name="q"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                strings?.labels?.search_placeholder || 'Cerca nel sito...'
              }
              autoComplete="off"
              className={cn(
                'text-large w-full border-b border-b-border-secondary-dark/40 bg-transparent py-spacing-2xs pr-[48px] font-helvetica font-light text-text-heading-invert-dark outline-none dark:text-text-heading-invert-light',
                'placeholder:text-text-heading-invert-dark/40 dark:placeholder:text-text-heading-invert-light/40',
                'focus:border-b-text-heading-invert-dark dark:focus:border-b-text-heading-invert-light',
                'transition-colors duration-300'
              )}
            />
            <button
              type="submit"
              aria-label={strings?.labels?.submit_search || 'Avvia ricerca'}
              className="absolute right-0 top-1/2 flex size-[48px] -translate-y-1/2 items-center justify-center text-text-heading-invert-dark transition-opacity hover:opacity-70 dark:text-text-heading-invert-light"
            >
              <SVGIcon symbol="search" className="size-[24px] fill-current" />
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

export default SearchOverlay
